/**
 * Ledger archive P0 (#2695, design v4.1 §1.3 / §1.5) — the constants and the
 * wire shape shared by the control-loops `ledger_archive` handler and the
 * platform route `POST /internal/ledger-archive/tick`.
 *
 * P0 only ADDS: money rows are copied to the trace bucket under
 * `ledger-archive/<env>/v1/`, nothing is ever deleted (from RDS or OSS), and
 * no existing reader or writer changes. Two keys gate everything and both
 * default off: `LEDGER_ARCHIVE_ARMED` (control-loops; off = no fetch at all)
 * and `LEDGER_ARCHIVE_ENABLED` + `LEDGER_ARCHIVE_STAGE` (platform; off = no
 * SQL at all).
 *
 * This module is the single source of truth for:
 *   - the object-key layout and its prefix (`ledger-archive/`, which
 *     `@nadoutong/oss-client` also guards — a platform test asserts the two
 *     agree);
 *   - the dispatch window and the heavy slots inside it (a control-loops
 *     schedule test pins them to the phases of the handlers they avoid);
 *   - the §1.3.3 canonical encoding table (the PR-B schema test and the
 *     exporter both read it from here);
 *   - the hot-window N / settle S numbers (§1.3.2).
 */

// ─── Identity ───────────────────────────────────────────────────────────────

export const LEDGER_ARCHIVE_HANDLER_NAME = 'ledger_archive';

/** Platform route the control-loops handler POSTs once per dispatched tick. */
export const LEDGER_ARCHIVE_TICK_PATH = '/internal/ledger-archive/tick';

export const LEDGER_ARCHIVE_ENVIRONMENTS = ['dev', 'staging', 'production'] as const;
export type LedgerArchiveEnvironment = (typeof LEDGER_ARCHIVE_ENVIRONMENTS)[number];

export function isLedgerArchiveEnvironment(value: unknown): value is LedgerArchiveEnvironment {
  return typeof value === 'string' && (LEDGER_ARCHIVE_ENVIRONMENTS as readonly string[]).includes(value);
}

/**
 * The only bucket an environment may write: its own trace bucket (09-28
 * decision: no separate bucket, a dedicated prefix). The AK is shared by all
 * three environments and can write any of them, so the exporter asserts this
 * before every write (`env_mismatch`).
 */
export function ledgerArchiveExpectedBucket(environment: LedgerArchiveEnvironment): string {
  return `router-message-traces-${environment}`;
}

/** `current_database()` each environment's platform must be connected to. */
export function ledgerArchiveExpectedDatabase(environment: LedgerArchiveEnvironment): string {
  return environment;
}

// ─── Object layout ──────────────────────────────────────────────────────────

/**
 * Root prefix of every ledger archive object. `@nadoutong/oss-client`
 * refuses DELETE and non-forbid-overwrite PUT under `OSS_PROTECTED_PREFIXES`;
 * this prefix must be one of them (asserted by a platform test and checked
 * again at runtime before every PUT).
 */
export const LEDGER_ARCHIVE_PREFIX = 'ledger-archive/';

export const LEDGER_ARCHIVE_LAYOUT_VERSION = 'v1';

/**
 * Other writers of the same trace bucket. None may be a prefix of the
 * ledger archive prefix or the other way round (a lifecycle rule or a
 * cleanup on theirs must never reach ours).
 */
export const LEDGER_ARCHIVE_TRACE_BUCKET_SIBLING_PREFIXES = Object.freeze([
  'message-traces/',
  'usage-logs-archive/',
  'usage-rated-events/',
] as const);

/** `ledger-archive/<env>/v1/` */
export function ledgerArchiveEnvironmentPrefix(environment: LedgerArchiveEnvironment): string {
  return `${LEDGER_ARCHIVE_PREFIX}${environment}/${LEDGER_ARCHIVE_LAYOUT_VERSION}/`;
}

export const LEDGER_ARCHIVE_KINDS = ['charge', 'unpaid', 'noncharge'] as const;
export type LedgerArchiveKind = (typeof LEDGER_ARCHIVE_KINDS)[number];

export const LEDGER_ARCHIVE_TABLES = [
  'balance_transactions',
  'revenue_ledger',
  'balance_charge_allocations',
] as const;
export type LedgerArchiveTable = (typeof LEDGER_ARCHIVE_TABLES)[number];

export interface LedgerArchivePartKey {
  readonly environment: LedgerArchiveEnvironment;
  /** Database identity segment, see {@link ledgerArchiveDatabasePrefix}. */
  readonly systemId: string;
  /** `YYYY-MM-DD`, as PostgreSQL printed the `date`. */
  readonly bookingDay: string;
  readonly rev: number;
  readonly gen: number;
  readonly kind: LedgerArchiveKind;
  readonly table: LedgerArchiveTable;
  readonly part: number;
}

/**
 * `ledger-archive/<env>/v1/sys=<system_identifier>/` — the objects written
 * from one database cluster (red team C12 / R-8).
 *
 * What this segment does and does not separate:
 *   - The three environments' databases live in ONE cluster (the shared Tokyo
 *     RDS), so they share one system_identifier. Cross-environment isolation
 *     rests on the `<env>` segment and on the `current_database()` ↔
 *     ENVIRONMENT assertion before every PUT — those are the primary defence.
 *   - A cluster built by `initdb` (a local stack, `pg_upgrade` to a new major
 *     version, a logical migration to a new instance) has a different
 *     identifier, so its generations and pub_seqs — which restart at 1 — can
 *     never land on the keys of another cluster.
 *   - A PHYSICAL copy keeps the identifier: HA switchover and in-place
 *     restore (the same cluster continuing, as intended), but also an Aliyun
 *     clone / PITR to a new instance. Such a clone run as the same
 *     environment with the shared AK writes the original's keys; only the
 *     write-once PUT (409 → object_conflict / chain_fork) stops it there.
 *
 * After a major upgrade or an instance migration the chain continues in the
 * database under a NEW `sys=` prefix; readers and restore drills follow the
 * manifests' `objects[].key` and `prev_sha256`, never a fixed prefix.
 */
export function ledgerArchiveDatabasePrefix(environment: LedgerArchiveEnvironment, systemId: string): string {
  if (!/^[0-9]{1,20}$/u.test(systemId)) throw new Error(`system identifier ${systemId} is not a decimal integer`);
  return `${ledgerArchiveEnvironmentPrefix(environment)}sys=${systemId}/`;
}

/**
 * `ledger-archive/<env>/v1/sys=<id>/day=<D>/rev=<r>/gen=<g>/<kind>/<tbl>/part-<nnnn>.ndjson.br`
 * (§1.5.3 step 3). The lease generation is in the key, so two generations
 * never write the same key.
 */
export function ledgerArchivePartKey(key: LedgerArchivePartKey): string {
  return `${ledgerArchiveDatabasePrefix(key.environment, key.systemId)}day=${key.bookingDay}/rev=${key.rev}/gen=${key.gen}/`
    + `${key.kind}/${key.table}/part-${String(key.part).padStart(4, '0')}.ndjson.br`;
}

/** `ledger-archive/<env>/v1/sys=<id>/manifests/<pub_seq, 12 digits>.json` (§1.5.3 step 4). */
export function ledgerArchiveManifestKey(environment: LedgerArchiveEnvironment, systemId: string, pubSeq: number): string {
  return `${ledgerArchiveDatabasePrefix(environment, systemId)}manifests/${String(pubSeq).padStart(12, '0')}.json`;
}

// ─── Encoding ───────────────────────────────────────────────────────────────

/** Bumped only with a reviewed change of the §1.3.3 table below. */
export const LEDGER_ARCHIVE_CANON_VERSION = 1;

/**
 * Fixed brotli quality (#2695 OSS size measurement, 09-28): Node's default
 * 11 compresses at ~1.4 MB/s on one core (a 10w-RPM day would take ~34 h);
 * 5 runs at ~131 MB/s for ~19% more bytes. Written into every part header.
 */
export const LEDGER_ARCHIVE_BROTLI_LEVEL = 5;

/** Plain (uncompressed) NDJSON bytes per part, header included. */
export const LEDGER_ARCHIVE_PART_MAX_PLAIN_BYTES = 64 * 1024 * 1024;

/**
 * Buffered plain bytes (all streams, headers included) at which the export
 * commits at once — compress, PUT, record, move the cursor — instead of
 * reading on. Keeps every commit small enough to finish inside any tick, so a
 * tick always makes progress (review H1 / red team C7), and keeps the main
 * thread's per-commit work (Buffer.concat, hashing slices) short (M2).
 */
export const LEDGER_ARCHIVE_COMMIT_PLAIN_BYTES = 8 * 1024 * 1024;

/**
 * Consecutive dispatched ticks that had due work but committed nothing
 * (deadline, brake, busy lock, blocked publish) before the tick is reported
 * `ok: false` (`stalled`) — design §1.6 "连续 10 次为黄".
 */
export const LEDGER_ARCHIVE_STALL_ALERT_TICKS = 10;

/**
 * Consecutive dispatched ticks lost to database disconnects / connection
 * acquisition timeouts (`transient_db`) before the tick is reported
 * `ok: false` (`transient_db_persistent`, red team R-2). One blip — an HA
 * switchover — is transient and silent; half an hour of runnable minutes of
 * it is not. Skipped minutes (outside the window, heavy slots) do not reset
 * it; a tick that makes progress does.
 */
export const LEDGER_ARCHIVE_TRANSIENT_ALERT_TICKS = 30;

/** A pause older than this is reported as `warning: 'paused_long: …'` on every tick (red team R-6). */
export const LEDGER_ARCHIVE_PAUSED_LONG_MS = 24 * 60 * 60 * 1000;

export type LedgerArchiveEncoding =
  | 'timestamptz_utc_us'
  | 'decimal_text'
  | 'float8_hex'
  | 'bool_tf'
  | 'raw_text';

/**
 * §1.3.3: the only column types an archive line can carry, keyed by
 * `format_type()` output without a type modifier. Anything else stops the
 * export (`unknown_column_type`) instead of being guessed.
 *
 *   timestamptz          to_char(x AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.US"Z"')
 *   bigint/integer/numeric  x::text (a JSON string: no JS precision loss)
 *   double precision     encode(float8send(x), 'hex')
 *   boolean              't' / 'f'
 *   text / json / jsonb  the text itself
 *   NULL                 JSON null
 */
export const LEDGER_ARCHIVE_CANONICAL_ENCODINGS: Readonly<Record<string, LedgerArchiveEncoding>> = Object.freeze({
  'timestamp with time zone': 'timestamptz_utc_us',
  bigint: 'decimal_text',
  integer: 'decimal_text',
  numeric: 'decimal_text',
  'double precision': 'float8_hex',
  boolean: 'bool_tf',
  text: 'raw_text',
  json: 'raw_text',
  jsonb: 'raw_text',
});

/** Encoding of a `format_type()` string (`numeric(12,2)` → numeric), or null. */
export function ledgerArchiveEncodingFor(formatType: string): LedgerArchiveEncoding | null {
  const base = formatType.replace(/\(.*\)$/u, '').trim();
  return Object.prototype.hasOwnProperty.call(LEDGER_ARCHIVE_CANONICAL_ENCODINGS, base)
    ? LEDGER_ARCHIVE_CANONICAL_ENCODINGS[base] ?? null
    : null;
}

// ─── Time (§1.3.2) ──────────────────────────────────────────────────────────

/**
 * S: a booking day D is due for export once `now() ≥ ledger_day_start(D + 1)
 * + S hours`. Hours, not days, so it never depends on a time zone.
 */
export const LEDGER_ARCHIVE_EXPORT_SETTLE_HOURS = 78;

/**
 * N: the hot window of the SHADOW cut line (user decision 2026-09-28: start
 * at 14 days, from the 60-day late-write age measurement). In P0 it only
 * decides which days the shadow ledgers would cut — nothing is ever deleted.
 */
export const LEDGER_ARCHIVE_HOT_WINDOW_DAYS_DEFAULT = 14;

/** `N_eff = max(N, ceil(S / 24) + 1)`: the cut line never passes an unexported day. */
export function ledgerArchiveEffectiveHotDays(
  hotDays: number = LEDGER_ARCHIVE_HOT_WINDOW_DAYS_DEFAULT,
  settleHours: number = LEDGER_ARCHIVE_EXPORT_SETTLE_HOURS,
): number {
  return Math.max(hotDays, Math.ceil(settleHours / 24) + 1);
}

// ─── Dispatch (§1.5.1) ──────────────────────────────────────────────────────

/** Shanghai is UTC+8 all year (no DST since 1991). */
export const LEDGER_ARCHIVE_SHANGHAI_UTC_OFFSET_MINUTES = 480;

/** Dispatch only in Shanghai [02:00, 07:00). */
export const LEDGER_ARCHIVE_WINDOW_START_MINUTE = 2 * 60;
export const LEDGER_ARCHIVE_WINDOW_END_MINUTE = 7 * 60;

/**
 * Minutes whose last digit is 3, 6 or 7 are skipped: residue 3 is the
 * reserved interval-5 vacancy (and `usage_logs_jp_rated_reverify`'s minute),
 * `rating_backstop_sweep`, the `usage_logs_jp_archive_copy/delete` pair and
 * `usage_logs_jp_rated_reap` sit on x6, `credit_draw_sweep` on x7. The last digit is the same in UTC and in Shanghai (the offset is a
 * multiple of ten minutes).
 */
export const LEDGER_ARCHIVE_SKIP_MINUTE_LAST_DIGITS = Object.freeze([3, 6, 7] as const);

/** `rating_events_cleanup` runs at :17 every hour. */
export const LEDGER_ARCHIVE_SKIP_MINUTES_OF_HOUR = Object.freeze([17] as const);

export interface LedgerArchiveSkipRange {
  /** Shanghai minute of day, inclusive. */
  readonly fromMinute: number;
  /** Shanghai minute of day, exclusive. */
  readonly toMinute: number;
  /** The control-loops handler whose run this range keeps clear of. */
  readonly handler: string;
  readonly reason: string;
}

/**
 * Fixed heavy slots inside the window (§1.5.1 table). The control-loops
 * schedule test walks the real HANDLERS registry and fails if any of these
 * handlers fires inside the window outside its range — moving a handler's
 * phase turns the test red instead of silently overlapping.
 */
export const LEDGER_ARCHIVE_SKIP_RANGES: readonly LedgerArchiveSkipRange[] = Object.freeze([
  { fromMinute: 2 * 60 + 5, toMinute: 2 * 60 + 15, handler: 'usage_logs_partitions', reason: 'partition DDL + drop-emptied + ANALYZE (360/9 = 02:09)' },
  { fromMinute: 2 * 60 + 30, toMinute: 2 * 60 + 45, handler: 'balance_source_health_refresh', reason: 'four full balance_transactions scans (360/36 = 02:36)' },
  { fromMinute: 4 * 60 + 15, toMinute: 4 * 60 + 25, handler: 'storage_snapshot', reason: 'relation-size snapshot (1440/1218 = 04:18)' },
]);

export interface LedgerArchiveRdsSkipRange {
  /** Shanghai minute of day, inclusive. */
  readonly fromMinute: number;
  /** Shanghai minute of day, exclusive. */
  readonly toMinute: number;
  readonly reason: string;
}

/**
 * Shared Tokyo RDS (`pgm-6wec1pg5feqhpbg6`, the database the export reads)
 * busy windows inside the dispatch window (design §1.2 item 12). Read with the
 * read-only Aliyun API on 2026-09-29 (`DescribeBackupPolicy`,
 * `DescribeBackups`):
 *   - backup window: Shanghai 03:00–04:00 daily, cloud-disk snapshot; the
 *     last 7 days started at 03:29 every day and ended 03:32:40–03:44:55 →
 *     skip 03:20–03:55;
 *   - maintenance window: Shanghai 02:00–06:00 daily, but it only acts when
 *     Aliyun has scheduled an operation event (HA switchover, ~30 s blip).
 *     Not skipped as a whole: a disconnect is handled as a transient error,
 *     and a scheduled event is covered by the ops pause (runbook
 *     `docs/runbooks/ledger-archive.md`).
 * Re-read after any change of the instance's backup policy.
 */
export const LEDGER_ARCHIVE_RDS_SKIP_RANGES: readonly LedgerArchiveRdsSkipRange[] = Object.freeze([
  { fromMinute: 3 * 60 + 20, toMinute: 3 * 60 + 55, reason: 'Tokyo RDS daily snapshot backup (03:29–03:45 observed 2026-09-22..28)' },
]);

export type LedgerArchiveSlot = 'run' | 'outside_window' | 'heavy_slot';

/** Shanghai minute of day [0, 1440) of an epoch minute. */
export function ledgerArchiveShanghaiMinuteOfDay(epochMinute: number): number {
  const shifted = epochMinute + LEDGER_ARCHIVE_SHANGHAI_UTC_OFFSET_MINUTES;
  return ((shifted % 1440) + 1440) % 1440;
}

/** Whether the tick at `epochMinute` may dispatch the archive (pure). */
export function ledgerArchiveSlot(epochMinute: number): LedgerArchiveSlot {
  const minute = ledgerArchiveShanghaiMinuteOfDay(epochMinute);
  if (minute < LEDGER_ARCHIVE_WINDOW_START_MINUTE || minute >= LEDGER_ARCHIVE_WINDOW_END_MINUTE) {
    return 'outside_window';
  }
  if ((LEDGER_ARCHIVE_SKIP_MINUTE_LAST_DIGITS as readonly number[]).includes(minute % 10)) return 'heavy_slot';
  if ((LEDGER_ARCHIVE_SKIP_MINUTES_OF_HOUR as readonly number[]).includes(minute % 60)) return 'heavy_slot';
  if ([...LEDGER_ARCHIVE_SKIP_RANGES, ...LEDGER_ARCHIVE_RDS_SKIP_RANGES].some((range) => minute >= range.fromMinute && minute < range.toMinute)) {
    return 'heavy_slot';
  }
  return 'run';
}

// ─── Budgets (§1.5.1 / §1.7) ────────────────────────────────────────────────

/** Per-tick cap: `budgetMs = min(this, tickBoundedBudgetMs(...))`. */
export const LEDGER_ARCHIVE_BUDGET_MS = 20_000;

/** Below this the handler yields the tick (`no_budget`) without a fetch. */
export const LEDGER_ARCHIVE_MIN_BUDGET_MS = 3_000;

/** control-loops `timeoutMs` = budget + this, so a normal finish is never killed. */
export const LEDGER_ARCHIVE_TIMEOUT_SLACK_MS = 5_000;

/** Every statement: `SET LOCAL statement_timeout = min(this, time left)`. */
export const LEDGER_ARCHIVE_STATEMENT_TIMEOUT_MS = 10_000;

/** Every OSS request: `timeoutMs = min(this, time left)`. */
export const LEDGER_ARCHIVE_OSS_REQUEST_TIMEOUT_MS = 15_000;

/** With less than this left the route commits what it has and answers `partial`. */
export const LEDGER_ARCHIVE_FINISH_RESERVE_MS = 1_000;

/** Daily DB-time budget of the daily legs (Shanghai run day). */
export const LEDGER_ARCHIVE_DAILY_DB_BUDGET_MS = 30 * 60_000;

/** Families (anchors) per page; one short transaction per page. */
export const LEDGER_ARCHIVE_PAGE_FAMILIES = 1000;

/** `braked:eventloop_lag` above this platform event-loop delay p99. */
export const LEDGER_ARCHIVE_EVENT_LOOP_P99_BRAKE_MS = 50;

// ─── Wire ───────────────────────────────────────────────────────────────────

export const LEDGER_ARCHIVE_STAGE_MAX = 4;

/**
 * Highest stage this build implements; a higher configured stage runs this
 * one and warns. 2 = export + verify (L2 / L3, shadow ledger, verify
 * manifest; #2695 PR-D); 3 = + L1, the sampled per (account, UTC hour)
 * identity on verified days (#2695 PR-E). STAGE 1 still runs the export
 * alone and STAGE 2 the export and the verify.
 */
export const LEDGER_ARCHIVE_STAGE_IMPLEMENTED = 3;

/**
 * STAGE 3: the L1 leg's share of the daily DB budget (v4 §1.7: L1 ≤ 10 of the
 * 30 minutes). Past it the leg yields until the next run day.
 */
export const LEDGER_ARCHIVE_L1_DAILY_DB_BUDGET_MS = 10 * 60_000;

/**
 * The L1 red sweep's share of backfill-class work, counted apart from the
 * daily budget (v4 §1.7: "另计 ≤ 30 分钟/晚"). It has its own counter,
 * `braked.sweep_db_ms`; the history leg keeps `backfill_db_ms`, so neither
 * order can spend or double-charge the other's share. The reserve is
 * `min(30, budget)`; at or below 30 it leaves no history room (turn the
 * sweep off or raise the budget). With the sweep off the old
 * `budget - backfill_db_ms` behavior is unchanged.
 */
export const LEDGER_ARCHIVE_BACKFILL_DB_BUDGET_MS = 30 * 60_000;

export type LedgerArchiveSkipReason =
  | 'disabled'
  | 'oss_unconfigured'
  | 'env_mismatch'
  | 'bucket_drift'
  | 'busy'
  | 'no_due_day'
  | 'chain_fork'
  | 'prefix_unprotected'
  | 'paused'
  | 'outside_window'
  | 'heavy_slot'
  | 'publish_blocked'
  /** A day holds an unpublished manifest reservation and is backing off: nothing else runs (stalled). */
  | 'publish_pending'
  /** A day holds an unpublished manifest reservation and is stopped on a terminal error (stalled). */
  | 'publish_held_by_terminal';

export type LedgerArchiveBrakeReason =
  | 'eventloop_lag'
  | 'budget_exhausted'
  | 'money_path_failed'
  | 'previous_tick_timeout'
  | 'rating_backlog'
  | 'allocation_backlog'
  | 'alloc_unknown'
  /** STAGE 3: the L1 leg spent its share of today's DB budget (neutral: tomorrow goes on). */
  | 'l1_budget_exhausted'
  /** STAGE 3: the red expansion's sweep spent its own 30-minute counter (neutral). */
  | 'backfill_budget_exhausted';

/** Error codes recorded in `ledger_archive_days.last_error` (§1.6). */
export type LedgerArchiveErrorCode =
  | 'oss_error'
  | 'object_conflict'
  | 'chain_fork'
  | 'unpaid_anchor_missing'
  | 'anchor_conflict'
  | 'l0_mismatch'
  | 'orphan'
  | 'unknown_column_type'
  | 'spec_incompatible'
  | 'plan_guard_refused'
  | 'statement_timeout'
  | 'deadline_abort'
  | 'lease_lost'
  | 'internal_error'
  // The verify (STAGE 2, #2695 PR-D). Terminal: the archive or the fold is
  // wrong, or the database moved in a way a re-export cannot follow.
  | 'readback_mismatch'
  | 'l3_mismatch'
  | 'fold_arith_error'
  | 'ref_incomplete'
  | 'verify_incoherent'
  | 'drift_repeated'
  | 'reexport_impossible'
  // Not terminal: the database no longer holds what was archived (L2).
  | 'l2_mismatch'
  // Not terminal once: the catalog moved under a verify statement (the next
  // tick re-plans); twice in a row it is spec_incompatible.
  | 'catalog_changed';

export interface LedgerArchiveTickRequest {
  /** Wall-clock the route may spend, measured from its arrival. */
  readonly budget_ms: number;
}

export interface LedgerArchiveTickResponse {
  readonly ok: boolean;
  /** Configured stage after parsing (0 when disabled / invalid). */
  readonly stage: number;
  readonly skipped?: LedgerArchiveSkipReason;
  readonly braked?: LedgerArchiveBrakeReason;
  /** The deadline cut the work short; progress is committed. */
  readonly partial?: boolean;
  readonly day?: string | null;
  readonly state?: string | null;
  readonly rev?: number | null;
  readonly gen?: number | null;
  readonly rows: number;
  readonly parts: number;
  readonly bytes_plain: number;
  readonly bytes_compressed: number;
  readonly manifest_pub_seq?: number | null;
  readonly db_ms: number;
  readonly error?: string;
  readonly config_warning?: string;
  /** Consecutive ticks with due work and no progress (`stalled` at the alert line). */
  readonly stall_streak?: number;
  /** Objects PUT this tick whose rows were never committed (unreferenced, never deleted). */
  readonly stray_objects?: number;
  /** Non-charged verdicts without an unpaid_ placeholder, as of the day's L0. */
  readonly unpaid_missing?: number;
  /** Consecutive ticks lost to database disconnects (`transient_db_persistent` at the alert line). */
  readonly transient_streak?: number;
  /** A verify tick (STAGE 2): the day's verify phase after the tick. */
  readonly verify_phase?: string;
  /** Archive bytes read back from OSS this tick (verify). */
  readonly oss_get_bytes?: number;
  /** An L1 tick (STAGE 3): the day's L1 phase after the tick (`day`, `sweep`, `done`). */
  readonly l1_phase?: string;
  /** An L1 tick: the day's `l1_state` after the tick. */
  readonly l1_state?: string;
  /** An L1 tick: UTC hours (day phase) or (account, day) units (sweep) checked this tick. */
  readonly l1_units?: number;
  /** An L1 tick: accounts found red this tick (count only; the ids are in ledger_drift_events). */
  readonly l1_red_accounts?: number;
  /** Operational warning that does not fail the tick (`paused_long: …`, `pause_row_missing: …`). */
  readonly warning?: string;
  /**
   * The platform process that answered (`PLATFORM_HOSTNAME`, else the OS
   * hostname — the container id in a container), so a tick can be traced to
   * its machine.
   */
  readonly host?: string;
}
